// The Missed calls page in words (owner request, PJ, 2026-10-24: "if I was not at my computer or I happen to miss that
// call, I want somewhere I could see all my missed calls so I can know who called at what time so I can call them
// back."). Pure: who called, when (my time and theirs, and how long ago), which of our numbers they rang, our last call
// to them before it, the row's state, and which number Call back dials. Unit-tested in tests/fd-missed-calls.test.ts;
// the page is web/src/pages/MissedCalls.tsx, Call back itself web/src/dialer/missedCallBack.ts.
import type { QueueItem } from '../../../src/shared/api';
import type { DialTarget } from '../../../src/shared/api-numbers';
import type {
  MatchLine, MissedActor, MissedCallRow, MissedCaller, MissedLastCall, MissedSettled, MissedStatus,
} from '../../../src/shared/api-missed';
import { DISPOSITION_META, fmtPhone } from './labels';
import { e164For, padTarget } from './numbers';
import { zonedParts } from './time';

const MIN = 60_000;
const HOUR = 3600_000;
const DAY = 86400_000;

/** The viewer's own zone (the browser's). */
export function myTz(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Toronto'; } catch { return 'America/Toronto'; }
}

/** "3:59 pm" in `tz`. */
export function clockWords(d: Date, tz: string): string {
  return d.toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' }).replace(/\s*([AP])M$/u, (_m, ap: string) => ` ${ap.toLowerCase()}m`);
}

function dayKey(d: Date, tz: string): number {
  const p = zonedParts(d, tz);
  return Date.UTC(p.year, p.month - 1, p.day);
}

/** "Today", "Yesterday" or "Oct 22" for `d` in `tz` (relative to `now` in the same zone). */
export function dayWords(d: Date, now: Date, tz: string): string {
  const diff = Math.round((dayKey(now, tz) - dayKey(d, tz)) / DAY);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString('en-US', { timeZone: tz, month: 'short', day: 'numeric' });
}

/** "Today 3:59 pm". */
export function dayClock(iso: string, now: Date, tz: string): string {
  const d = new Date(iso);
  return `${dayWords(d, now, tz)} ${clockWords(d, tz)}`;
}

/** "just now", "12 min ago", "3 h ago", "2 days ago". */
export function agoWords(iso: string, now: Date): string {
  const ms = now.getTime() - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < MIN) return 'just now';
  if (ms < HOUR) return `${Math.floor(ms / MIN)} min ago`;
  if (ms < DAY) return `${Math.floor(ms / HOUR)} h ago`;
  const days = Math.floor(ms / DAY);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

function zoneAbbrev(d: Date, tz: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(d).find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch { return ''; }
}

/**
 * When they called (PJ 2026-10-24: "who called at what time"): my time ("Today 3:59 pm"), theirs when their clock reads
 * differently ("12:59 pm their time (PDT)", with the day when it differs from mine), and how long ago.
 */
export function whenWords(iso: string, now: Date, opts: { mine: string; theirs: string | null }): { mine: string; theirs: string | null; ago: string } {
  const d = new Date(iso);
  const mine = dayClock(iso, now, opts.mine);
  let theirs: string | null = null;
  if (opts.theirs) {
    try {
      const myClock = clockWords(d, opts.mine);
      const theirClock = clockWords(d, opts.theirs);
      const myDay = dayWords(d, now, opts.mine);
      const theirDay = dayWords(d, now, opts.theirs);
      if (theirClock !== myClock || theirDay !== myDay) {
        const abbr = zoneAbbrev(d, opts.theirs);
        theirs = `${theirDay !== myDay ? `${theirDay} ` : ''}${theirClock} their time${abbr ? ` (${abbr})` : ''}`;
      }
    } catch { theirs = null; }
  }
  return { mine, theirs, ago: agoWords(iso, now) };
}

/** "your 707 line": which of our numbers they called (its area code). */
export function ourLineWords(our: MissedCallRow['ourNumber']): string | null {
  const m = our ? /^\+1(\d{3})\d{7}$/.exec(our.e164) : null;
  return m ? `your ${m[1]} line` : our ? `your ${fmtPhone(our.e164)} line` : null;
}

const LINE_WORDS: Record<MatchLine, string> = {
  owner: "the owner's line",
  store: 'the store line',
  main: 'the number on the lead',
  other: 'a number we had for them',
};

const place = (c: Pick<MissedCaller, 'name' | 'city'>): string => (c.city ? `${c.name} (${c.city})` : c.name);

/** "A, B and C" (up to `max` names, then "and N more"). */
export function nameList(names: string[], max = 6): string {
  if (names.length <= 1) return names[0] ?? '';
  const shown = names.slice(0, max);
  const more = names.length - shown.length;
  if (more > 0) return `${shown.join(', ')} and ${more} more`;
  return `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}`;
}

/**
 * Who called: the business and city with the line it matched on ("Matched on the owner's line"); several businesses
 * holding the number listed ("One of 3 stores" / "This number is on 3 of our leads: ..."); else "Unknown number", or
 * "Number hidden".
 */
export function callerWords(row: Pick<MissedCallRow, 'caller' | 'candidates' | 'fromE164'>): { title: string; line: string | null } {
  if (row.caller) return { title: place(row.caller), line: `Matched on ${LINE_WORDS[row.caller.line]}` };
  if (row.candidates.length > 1) {
    const owner = row.candidates.every((c) => c.line === 'owner');
    return {
      title: owner ? `One of ${row.candidates.length} stores` : `One of ${row.candidates.length} leads`,
      line: `${owner ? "This number is the owner's line for" : 'This number is on'} ${nameList(row.candidates.map(place))}`,
    };
  }
  return { title: row.fromE164 ? 'Unknown number' : 'Number hidden', line: null };
}

/** "You", "Max", "Valistix" (an admin while admins' activity is hidden), or `fallback`. */
export function actorWords(a: MissedActor | null | undefined, fallback = 'Someone'): string {
  if (!a) return fallback;
  return a.isMe ? 'You' : a.name ?? fallback;
}

const lower = (s: string): string => (s === 'You' ? 'you' : s);

/** "You called them Oct 22 2:30 pm, logged No answer"; null when we never called that number before. */
export function lastCallWords(last: MissedLastCall | null, now: Date, tz: string): string | null {
  if (!last) return null;
  const outcome = last.outcome ? DISPOSITION_META[last.outcome]?.label ?? last.outcome : null;
  return `${actorWords(last.by, 'We')} called them ${dayClock(last.at, now, tz)}, ${outcome ? `logged ${outcome}` : 'no outcome logged'}`;
}

/** The row's state in words: "Called back by Max, Today 4:10 pm", "They called again and you answered, ...", "Done by
 *  PJ, Oct 22 5:00 pm: wrong number, a robocall", "Done: they're on the do-not-call list". Null while open. */
export function settledWords(s: MissedSettled | null, now: Date, tz: string): string | null {
  if (!s) return null;
  const when = s.at ? `, ${dayClock(s.at, now, tz)}` : '';
  switch (s.how) {
    case 'called_back': return `Called back by ${lower(actorWords(s.by))}${when}`;
    case 'answered': return `They called again and ${s.by ? lower(actorWords(s.by)) : 'we'} answered${when}`;
    case 'dnc': return "Done: they're on the do-not-call list";
    default: return `Done by ${lower(actorWords(s.by))}${when}${s.note ? `: ${s.note}` : ''}`;
  }
}

export const STATUS_CHIP: Record<MissedStatus, { label: string; cls: string }> = {
  open: { label: 'Open', cls: 'chip chip-bad' },
  called_back: { label: 'Called back', cls: 'chip chip-ok' },
  done: { label: 'Done', cls: 'chip' },
};

/** "called 3 times" for a folded row; null for one call. */
export function timesWords(n: number): string | null {
  return n > 1 ? `called ${n} times` : null;
}

/** Who the call rang (the Everyone view): "Rang Max", "Rang you", "Rang the admins" (no one in particular). */
export function rangWords(rang: MissedActor | null): string {
  return rang ? `Rang ${lower(actorWords(rang))}` : 'Rang the admins';
}

/** "0:42" for a voicemail's length. */
export function vmLength(sec: number | null): string | null {
  if (sec === null || !Number.isFinite(sec) || sec < 0) return null;
  return `${Math.floor(sec / 60)}:${String(Math.round(sec % 60)).padStart(2, '0')}`;
}

/**
 * Which of the lead's numbers Call back dials (PJ 2026-10-24: "dials the number they called from"): the owner's line or
 * the store line by number (as the dial pad does, numbers.ts padTarget: null = the lead's default, which is that number),
 * or `why` when the number they called from is none of the lead's numbers now (logged wrong or replaced since).
 */
export function callBackTarget(item: QueueItem, fromE164: string): { target: DialTarget | null; why: null } | { target: null; why: string } {
  const target = padTarget(item, fromE164);
  if (e164For(item, target) === fromE164) return { target, why: null };
  return { target: null, why: `They called from ${fmtPhone(fromE164)}, which isn't one of this lead's numbers any more, so it wasn't dialled. Check the lead, or call that number from the dial pad.` };
}

/** The Call back button's label: "Call back (707) 555-0100". */
export function callBackLabel(row: Pick<MissedCallRow, 'fromE164'>): string {
  return row.fromE164 ? `Call back ${fmtPhone(row.fromE164)}` : 'Call back';
}
